import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { storageKeys, TEXT_STRING } from '../../constants';
import Button from 'components/Button';
import {
  detectMobile,
  detectTutorialPlatform,
  trackPwaGuideClose,
  trackPwaGuideOpen,
} from 'utils';
import { TTutorialPlatform, TTutorialTrigger } from 'types';
import { useSettingStore, useUserStore } from 'store';
import IphoneSafariGuide from '../../assets/images/tutorial/iphone-safari-pwa-guide.webp';
import IphoneChromeGuide from '../../assets/images/tutorial/iphone-chrome-pwa-guide.webp';
import AndroidGuide from '../../assets/images/tutorial/android-pwa-guide.webp';

const STR = TEXT_STRING.PWA_GUIDE;

const GUIDE_IMAGES: Record<TTutorialPlatform, { src: string; alt: string }> = {
  iphone_safari: { src: IphoneSafariGuide, alt: STR.IMAGE_ALT_IPHONE_SAFARI },
  iphone_chrome: { src: IphoneChromeGuide, alt: STR.IMAGE_ALT_IPHONE_CHROME },
  android: { src: AndroidGuide, alt: STR.IMAGE_ALT_ANDROID },
};

type Props = {
  onClose?: () => void;
  text?: { title?: string };
  // 渡さないときはストアの openTutorial で開閉する
  open?: boolean;
  // 渡さないときはストアの tutorialTrigger を使う
  trigger?: TTutorialTrigger;
};

function TutorialModal({
  onClose,
  text = {},
  open,
  trigger,
}: Props) {
  // Hook
  const { openTutorial, setOpenTutorial, tutorialTrigger } = useSettingStore();
  const userId = useUserStore((state) => state.user?.id);

  // Memo
  const panelRef = useRef(null);
  const isMobile = useMemo(() => detectMobile(), []);
  const platform = useMemo(() => detectTutorialPlatform(), []);
  const show = open ?? openTutorial;
  const currentTrigger = trigger ?? tutorialTrigger;

  // 開いた回ごとに 1 回だけ送る
  const isOpenTrackedRef = useRef(false);

  useEffect(() => {
    if (!show) {
      isOpenTrackedRef.current = false;
      return;
    }
    if (isOpenTrackedRef.current || !currentTrigger) return;
    isOpenTrackedRef.current = true;
    trackPwaGuideOpen({ trigger: currentTrigger, platform, userId });
  }, [show, currentTrigger, platform, userId]);

  const onConfirm = useCallback(() => {
    if (currentTrigger) {
      trackPwaGuideClose({ trigger: currentTrigger, platform, userId });
    }
    localStorage.setItem(storageKeys.IS_CONFIRM_TUTORIAL, 'true');
    setOpenTutorial(false);
    onClose?.();
  }, [setOpenTutorial, onClose, currentTrigger, platform, userId]);

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-20"
        initialFocus={panelRef}
        onClose={() => {}}
        aria-label={text.title ? undefined : GUIDE_IMAGES[platform].alt}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div data-testid="tutorial-backdrop" className="fixed inset-0 bg-black bg-opacity-60" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-end justify-center overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-out duration-300"
            enterFrom="translate-y-full"
            enterTo="translate-y-0"
            leave="transform transition ease-in duration-200"
            leaveFrom="translate-y-0"
            leaveTo="translate-y-full"
          >
            <Dialog.Panel
              ref={panelRef}
              className={`${isMobile ? 'w-full' : 'w-[50vh]'} max-w-full max-h-[90dvh] flex flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl`}
            >
              <div aria-hidden="true" className="shrink-0 py-3">
                <div data-testid="tutorial-handle" className="mx-auto h-1 w-10 rounded-full bg-gray-300" />
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                {text.title && (
                  <Dialog.Title className="px-8 pt-2 pb-2 text-sm font-bold">
                    {text.title}
                  </Dialog.Title>
                )}
                <img
                  src={GUIDE_IMAGES[platform].src}
                  alt={GUIDE_IMAGES[platform].alt}
                  className="block w-full h-auto"
                />
              </div>
              <div className="shrink-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <Button onClick={onConfirm} className="p-4 w-full" bold>
                  {TEXT_STRING.COMMON.CLOSE}
                </Button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default TutorialModal;
